import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { friendsAPI } from '../services/api';
import { io, Socket } from 'socket.io-client';
import { useTranslation } from 'react-i18next';

interface PendingRequest {
  id: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  isSent: boolean;
}

const Invitations: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [socket, setSocket] = useState<Socket | null>(null);

  useEffect(() => {
    loadPendingRequests();
    
    // Initialize socket connection for notifications
    if (user) {
      const token = localStorage.getItem('token');
      if (token) {
        const newSocket = io('/', {
          auth: { token },
        });

        newSocket.on('connect', () => {
          console.log('Invitations: Connected to socket');
          newSocket.emit('joinUserRoom', { userId: user.id });
        });

        newSocket.on('friendRequestReceived', (data) => {
          console.log('Friend request received:', data);
          setSuccess(t('invitations.newRequestFrom', { name: `${data.from.firstName} ${data.from.lastName}` }));
          loadPendingRequests(); // Refresh data to show new request
        });

        newSocket.on('friendRequestAccepted', (data) => {
          console.log('Friend request accepted:', data);
          setSuccess(t('invitations.acceptedYourRequest', { name: `${data.from.firstName} ${data.from.lastName}` }));
          loadPendingRequests(); // Refresh data
        });

        newSocket.on('friendRequestRejected', (data) => {
          console.log('Friend request rejected:', data);
          loadPendingRequests(); // Refresh data
        });

        setSocket(newSocket);

        return () => {
          newSocket.close();
        };
      }
    }
  }, [user]);

  const loadPendingRequests = async () => {
    try {
      const response = await friendsAPI.getPendingRequests();
      console.log('Loaded pending requests:', response.data);
      
      // Filter to show only received requests (not sent ones)
      const receivedRequests = response.data.filter((req: PendingRequest) => !req.isSent);
      setPendingRequests(receivedRequests);
    } catch (error) {
      console.error('Error loading pending requests:', error);
      setError(t('invitations.failedLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (requestId: string) => {
    try {
      await friendsAPI.acceptRequest(requestId);
      setSuccess(t('invitations.acceptedMsg'));
      setError('');
      loadPendingRequests(); // Refresh the list
    } catch (error) {
      console.error('Error accepting request:', error);
      setError(t('invitations.failedAccept'));
      setSuccess('');
    }
  };

  const handleReject = async (requestId: string) => {
    try {
      await friendsAPI.rejectRequest(requestId);
      setSuccess(t('invitations.rejectedMsg'));
      setError('');
      loadPendingRequests(); // Refresh the list
    } catch (error) {
      console.error('Error rejecting request:', error);
      setError(t('invitations.failedReject'));
      setSuccess('');
    }
  };

  // Auto-clear messages after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  return (
    <div className="dashboard-modern">
      {/* Invitations Header */}
      <div className="dashboard-header">
        <div>
          <h1>📨 {t('invitations.header')} ({pendingRequests.length})</h1>
          <p>{t('invitations.subtitle')}</p>
        </div>
      </div>

      {/* Invitations Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value" style={{ color: '#FF9800' }}>{pendingRequests.length}</div>
          <div className="stat-label">{t('invitations.pendingRequests')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: '#4CAF50' }}>{t('invitations.ready')}</div>
          <div className="stat-label">{t('invitations.status')}</div>
        </div>
      </div>

      {/* Notifications */}
      {error && (
        <div style={{
          background: 'rgba(244, 67, 54, 0.1)',
          border: '1px solid #f44336',
          color: '#f44336',
          padding: '15px 20px',
          borderRadius: '12px',
          marginBottom: '20px',
          fontSize: '14px'
        }}>
          ❌ {error}
        </div>
      )}

      {success && (
        <div style={{
          background: 'rgba(76, 175, 80, 0.1)',
          border: '1px solid #4CAF50',
          color: '#4CAF50',
          padding: '15px 20px',
          borderRadius: '12px',
          marginBottom: '20px',
          fontSize: '14px'
        }}>
          ✅ {success}
        </div>
      )}

      {/* Friend Requests Section */}
      <div className="game-section">
        <div className="section-header">
          <h3 className="section-title">
            <span className="section-icon">📬</span>
            {t('invitations.header')} ({pendingRequests.length})
          </h3>
        </div>

        {loading ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px',
            color: 'rgba(255,255,255,0.6)'
          }}>
            <div className="spinner-border text-light" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p style={{ marginTop: '20px' }}>{t('invitations.loadingInvitations')}</p>
          </div>
        ) : pendingRequests.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '80px 40px', 
            color: 'rgba(255,255,255,0.7)',
            background: 'rgba(255,255,255,0.02)',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.1)'
          }}>
            <div style={{ 
              fontSize: '64px', 
              marginBottom: '25px', 
              opacity: 0.3
            }}>
              📭
            </div>
            <h4 style={{ 
              color: '#fff', 
              marginBottom: '15px',
              fontSize: '20px',
              fontWeight: '600'
            }}>
              {t('invitations.noPendingTitle')}
            </h4>
            <p style={{ 
              margin: '0', 
              fontSize: '16px', 
              lineHeight: '1.6',
              opacity: 0.8
            }}>
              {t('invitations.noPendingDesc1')}<br />
              {t('invitations.noPendingDesc2')}
            </p>
          </div>
        ) : (
          <div className="games-grid">
            {pendingRequests.map((request) => (
              <div key={request.id} className="game-card modern">
                <div className="game-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div className="user-avatar" style={{ width: '50px', height: '50px', fontSize: '20px' }}>
                      {request.user.firstName[0].toUpperCase()}
                    </div>
                    <div>
                      <h4 className="game-name" style={{ margin: '0 0 5px 0' }}>
                        {request.user.firstName} {request.user.lastName}
                      </h4>
                      <p style={{ 
                        color: 'rgba(255,255,255,0.6)', 
                        fontSize: '14px', 
                        margin: 0 
                      }}>
                        {request.user.email}
                      </p>
                    </div>
                  </div>
                  <span className="game-status waiting">
                    {t('invitations.new')}
                  </span>
                </div>
                
                <div className="game-info">
                  <div className="info-row">
                    <span>{t('invitations.received')}:</span>
                    <span>{new Date(request.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div className="info-row">
                    <span>{t('invitations.time')}:</span>
                    <span>{new Date(request.createdAt).toLocaleTimeString()}</span>
                  </div>
                </div>

                <div className="game-actions">
                  <button 
                    onClick={() => handleAccept(request.id)}
                    className="btn-join"
                    style={{ flex: 1 }}
                  >
                    ✅ {t('invitations.accept')}
                  </button>
                  <button 
                    onClick={() => handleReject(request.id)}
                    className="btn-delete"
                    style={{ flex: 1 }}
                  >
                    ❌ {t('invitations.reject')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Invitations; 